import type { Publication } from "@rowan/prisma";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { trpc } from "../../../utils/trpc";
import { LoadingSpinner } from "../../bits/loading";
import { ArchivedPage } from "../../pages/ArchivedPage";
import { ErrorPage } from "../../pages/ErrorPage";
import { NotFoundPage } from "../../pages/NotFoundPage";
import {
  extractChapterKey,
  getHeadingParent,
  getSectionFromContents,
  useDebounce,
} from "../../utils";
import {
  deleteCachedPublication,
  getCacheItem,
  setCacheItem,
  trySetCacheItemWithEviction,
} from "../../utils/indexedDB";
import type { ContentsItem } from "./PublicationContext";
import { PublicationContext } from "./PublicationContext";

export const PublicationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { publicationSlug } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);

  const [shouldFetch, setShouldFetch] = useState(false);
  const [contents, setContents] = useState<ContentsItem[]>();
  const [chapters, setChapters] = useState<Record<string, string>>();
  const [styles, setStyles] = useState<string>();
  const [htmlChunks, setHtmlChunks] = useState<Record<number, string>>();
  const [currentSection, setCurrentSection] = useState<ContentsItem>();
  const [currentChunk, setCurrentChunk] = useState<string>();
  const [scrollDisabled, setScrollDisabled] = useState<boolean>();
  const [scrollSnippet, setScrollSnippet] = useState<string>();
  const [publication, setPublication] = useState<Publication>();
  const [pendingHash, setPendingHash] = useState<string>();

  const {
    data: publicationData,
    isLoading,
    error: getPublicationError,
  } = trpc.getPublication.useQuery(
    {
      publicationSlug: publicationSlug as string,
    },
    {
      enabled: !!publicationSlug,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      cacheTime: 0,
      retry: (failureCount, error) => {
        if (failureCount >= 3) return false; // Max 3 retries
        if (!error.data) return true; // Retry on network errors
        if (String(error.data.httpStatus).startsWith("4")) return false; // Don't retry on 400 errors
        return true;
      },
    },
  );

  const { data: metadata, error } = trpc.getPublicationMetadata.useQuery(
    {
      publicationId: publicationSlug as string,
    },
    {
      enabled: !!publicationSlug && !!publication,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false,
    },
  );

  const { data: content } = trpc.getPublicationContent.useQuery(
    {
      publicationId: publicationSlug as string,
    },
    {
      enabled: !!publicationSlug && shouldFetch,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  );

  const { mutate: mutateRecentPublication } =
    trpc.recentPublication.useMutation();

  const loadingChunks = useRef<Set<number>>(new Set());

  const loadChunk = useCallback(
    async (position: number) => {
      if (
        htmlChunks?.[position] ||
        !publicationSlug ||
        !Object.values(chapters || {}).includes(`${position}`) ||
        loadingChunks.current.has(position)
      )
        return;

      loadingChunks.current.add(position);
      const key = `${publicationSlug}/chunks/${position}`;
      const cached = await getCacheItem(key);
      if (!cached?.contents) {
        loadingChunks.current.delete(position);
        setShouldFetch(true);
        return;
      }
      setHtmlChunks((prev) => ({ ...prev, [position]: cached.contents }));
      loadingChunks.current.delete(position);
    },
    [publicationSlug, htmlChunks, chapters],
  );

  const selectByHash = useCallback((hash: string) => {
    if (window.location.hash.slice(1) === hash) return;
    setScrollDisabled(true);
    window.location.hash = `#${hash}`;
  }, []);

  const selectSection = useCallback(
    (id: string, section?: ContentsItem, noScroll = false) => {
      if (!contents) return;
      const contentsSection = section || getSectionFromContents(contents, id);
      if (contentsSection?.id && !contentsSection?.label) {
        const heading = getHeadingParent(contentsSection?.id, contents);
        contentsSection.label = heading?.label || "";
      }
      setCurrentSection(contentsSection);
      const chapterKey = extractChapterKey(id);
      const position = chapterKey ? chapters?.[chapterKey] : "0";
      if (position && position !== currentChunk) {
        setCurrentChunk(position);
      }

      if (noScroll) {
        history.replaceState(null, "", `#${id}`);
      } else {
        window.location.hash = id;
      }
    },
    [chapters, contents, currentChunk],
  );

  useEffect(() => {
    const handleHashChange = async () => {
      const hash = window.location.hash.slice(1);
      const chapterKey = extractChapterKey(hash);
      if (!chapterKey || !chapters) return;
      const positionStr = chapters[chapterKey];
      if (!positionStr) return;

      const position = +positionStr;
      setScrollDisabled(true);
      await Promise.all([
        loadChunk(position - 1),
        loadChunk(position),
        loadChunk(position + 1),
      ]);
      setPendingHash(hash);
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters]);

  useEffect(() => {
    if (publicationData && publicationSlug) {
      const publicationWithDates = {
        ...publicationData,
        lastModified: new Date(publicationData.lastModified),
        createdAt: new Date(publicationData.createdAt),
      };

      // Check if publication is already in cache
      const updateCache = async () => {
        const cachedPublication = await getCacheItem(
          `${publicationSlug}/publication`,
        );
        if (cachedPublication?.contents) {
          const parsedPublication = JSON.parse(cachedPublication.contents);
          if (parsedPublication.version !== publicationData.version) {
            // if the version is different, clear the cache for all data and save the new one
            await deleteCachedPublication(publicationSlug);
            await setCacheItem(
              `${publicationSlug}/publication`,
              JSON.stringify(publicationWithDates),
              publicationSlug,
            );
          }
          // else we can use the cached publication since they are the same
          // so do nothing
        } else {
          // if the publication is not in cache, we can save the new one
          await setCacheItem(
            `${publicationSlug}/publication`,
            JSON.stringify(publicationWithDates),
            publicationSlug,
          );
        }
        setPublication(publicationWithDates);
      };
      updateCache();
    } else if (!isLoading) {
      if (
        getPublicationError &&
        getPublicationError.message === "Publication is archived" &&
        publicationSlug
      ) {
        // If the publication is archived, we will clear the cache
        deleteCachedPublication(publicationSlug);
      } else {
        const getCachedPublication = async () => {
          const cachedPublication = await getCacheItem(
            `${publicationSlug}/publication`,
          );
          if (cachedPublication?.contents) {
            const parsedPublication = JSON.parse(cachedPublication.contents);
            setPublication(parsedPublication);
          }
        };
        getCachedPublication();
      }
    }
  }, [publicationData, publicationSlug, isLoading, getPublicationError]);

  useEffect(() => {
    if (currentChunk) {
      const loadSurroundingChunks = async () => {
        const toLoad = [+currentChunk - 1, +currentChunk + 1];
        await Promise.all(toLoad.map((chunk) => loadChunk(chunk)));
      };

      loadSurroundingChunks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChunk]);

  useEffect(() => {
    const handleMetadata = async (pubId: string) => {
      await setCacheItem(`meta:${pubId}`, "", pubId, Date.now());
      const cachedContents = await getCacheItem(`${pubId}/contents`);
      const cachedChapters = await getCacheItem(`${pubId}/chapters`);
      const styles = await getCacheItem(`${pubId}/styles`);
      if (metadata) {
        if (
          !cachedContents?.contents ||
          !cachedChapters?.contents ||
          // contents might be an empty string which is valid
          styles?.contents !== undefined
        ) {
          const fetchedContents = JSON.parse(metadata.contents);
          const saveMetadata = async (
            metadata?: Record<string | number, string>,
          ) => {
            await setCacheItem(
              `${pubId}/contents`,
              metadata ? metadata.contents : "",
              pubId,
            );
            await setCacheItem(
              `${pubId}/chapters`,
              metadata ? metadata.chapters : "",
              pubId,
            );
            // contents might be an empty string which is valid
            if (metadata?.styles !== undefined) {
              await setCacheItem(
                `${pubId}/styles`,
                metadata ? metadata.styles : "",
                pubId,
              );
              setStyles(metadata.styles);
            }
          };
          saveMetadata(metadata);
          setChapters(JSON.parse(metadata.chapters));
          setContents(fetchedContents);
        } else {
          setChapters(JSON.parse(metadata.chapters));
          setContents(JSON.parse(cachedContents.contents));
          setStyles(metadata.styles);
        }
      } else {
        if (
          cachedChapters?.contents &&
          // contents might be an empty string which is valid
          styles?.contents !== undefined &&
          cachedContents?.contents
        ) {
          setStyles(styles?.contents);
          setChapters(JSON.parse(cachedChapters?.contents ?? "{}"));
          setContents(JSON.parse(cachedContents?.contents ?? "{}"));
        } else setShouldFetch(true);
      }
    };
    if (!publicationSlug || !publication) return;
    handleMetadata(publicationSlug);
  }, [metadata, error, publicationSlug, publication]);

  useEffect(() => {
    if (!chapters || !publicationSlug) return;
    const hash = window.location.hash.slice(1);
    const chapterKey = extractChapterKey(hash);
    const position = chapterKey ? chapters[chapterKey] : "0";
    const loadInitialFromChapterId = async () => {
      if (!chapterKey)
        window.location.replace(
          window.location.href + `#${publicationSlug}-ch:0`,
        );
      setCurrentChunk(position);
      await Promise.all([
        loadChunk(+position - 1),
        loadChunk(+position),
        loadChunk(+position + 1),
      ]);
    };
    loadInitialFromChapterId();

    const section = getSectionFromContents(
      contents || [],
      hash || `${publicationSlug}-ch:0`,
    );
    setCurrentSection(section);
  }, [chapters, publicationSlug, loadChunk, contents]);

  useEffect(() => {
    if (content && publicationSlug) {
      const nextChunks: Record<number, string> = {};
      const saveChunks = async () => {
        await Promise.all(
          content.map(async (chunk, key) => {
            const position = key;
            const chunkKey = `${publicationSlug}/chunks/${position}`;
            await trySetCacheItemWithEviction(
              chunkKey,
              chunk.contents || "",
              publicationSlug,
            );
            nextChunks[position] = chunk.contents || "";
          }),
        );
        const hash = window.location.hash.slice(1);
        const chapterKey = extractChapterKey(hash);
        if (!chapterKey) return;
        const position = chapters?.[chapterKey];
        if (position) {
          await loadChunk(+position);
        }
      };
      saveChunks();
    }
    // Does not need to include publicationSlug as content will update if changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const debounceRecentPublication = useDebounce(
    (currentSectionId: string, publicationId: number) =>
      mutateRecentPublication({
        publicationId: publicationId,
        location: currentSectionId,
      }),
    1000,
  );

  useEffect(() => {
    if (currentSection?.id && publication) {
      debounceRecentPublication(currentSection.id, publication.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection?.id, publication]);

  const context = useMemo(
    () => ({
      contents,
      chapters,
      styles,
      htmlChunks,
      publication: publication as Publication,
      publicationSlug,
      currentSection,
      selectSection,
      currentChunk,
      selectByHash,
      scrollDisabled,
      setScrollDisabled,
      scrollSnippet,
      setScrollSnippet,
      pendingHash,
      setPendingHash,
      containerRef,
    }),
    [
      contents,
      chapters,
      styles,
      htmlChunks,
      publication,
      publicationSlug,
      currentSection,
      selectByHash,
      selectSection,
      currentChunk,
      scrollDisabled,
      scrollSnippet,
      setScrollSnippet,
      pendingHash,
      setPendingHash,
      containerRef,
    ],
  );

  if (
    getPublicationError &&
    getPublicationError.message === "Publication is archived"
  ) {
    return <ArchivedPage />;
  }

  if (
    getPublicationError &&
    getPublicationError.message === "Publication not found"
  ) {
    return <NotFoundPage message="This publication could not be found." />;
  }

  if (getPublicationError)
    return <ErrorPage message={getPublicationError.message} />;

  if (!context.publication) return <LoadingSpinner />;

  return (
    <PublicationContext.Provider value={context}>
      {children}
    </PublicationContext.Provider>
  );
};
